import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ChainsService } from 'src/app/services/chains.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-chain-catalog',
  templateUrl: './chain-catalog.component.html',
  styleUrls: ['./chain-catalog.component.scss']
})
export class ChainCatalogComponent implements OnInit {
  chainId: string = '';

  // ── Categories ─────────────────────────────────────
  categories: any[] = [];
  categoryLoading = false;
  showCategoryForm = false;
  editingCategory: any = null;
  categoryForm!: FormGroup;

  // ── Products ────────────────────────────────────────
  products: any[] = [];
  productLoading = false;
  showProductForm = false;
  editingProduct: any = null;
  productForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private chainsService: ChainsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.chainId = this.route.snapshot.params['chainId'] || '';
    this.initForms();
    this.loadCategories();
    this.loadProducts();
  }

  private initForms(): void {
    this.categoryForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      slug: new FormControl(''),
      description: new FormControl(''),
      sortOrder: new FormControl(0, [Validators.min(0)])
    });

    this.productForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      description: new FormControl(''),
      barcode: new FormControl(''),
      price: new FormControl(0, [Validators.required, Validators.min(0)]),
      categoryId: new FormControl(''),
      isVisible: new FormControl(true)
    });
  }

  // ── Category helpers ────────────────────────────────

  loadCategories(): void {
    if (!this.chainId) return;
    this.categoryLoading = true;
    this.spinner.show();
    this.chainsService.getChainCategories(this.chainId).subscribe({
      next: (res: any) => {
        this.categories = res.response?.data?.categories || res.response?.data || res.categories || [];
        this.categoryLoading = false;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.categoryLoading = false;
        this.spinner.hide();
        if (err.status !== 404) {
          this.toastr.error(err.error?.response?.message || 'Failed to load categories');
        }
      }
    });
  }

  openAddCategory(): void {
    this.editingCategory = null;
    this.categoryForm.reset({ name: '', slug: '', description: '', sortOrder: 0 });
    this.showCategoryForm = true;
  }

  openEditCategory(cat: any): void {
    this.editingCategory = cat;
    this.categoryForm.patchValue({
      name: cat.name,
      slug: cat.slug || '',
      description: cat.description || '',
      sortOrder: cat.sortOrder || 0
    });
    this.showCategoryForm = true;
  }

  cancelCategoryForm(): void {
    this.showCategoryForm = false;
    this.editingCategory = null;
    this.categoryForm.reset();
  }

  onCategoryNameChange(): void {
    const name: string = this.categoryForm.get('name')?.value || '';
    const currentSlug: string = this.categoryForm.get('slug')?.value || '';
    if (!this.editingCategory && !currentSlug) {
      this.categoryForm.patchValue({ slug: this.slugify(name) });
    }
  }

  submitCategoryForm(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    const payload = {
      name: this.categoryForm.get('name')!.value,
      slug: this.categoryForm.get('slug')!.value || this.slugify(this.categoryForm.get('name')!.value),
      description: this.categoryForm.get('description')!.value || undefined,
      sortOrder: this.categoryForm.get('sortOrder')!.value || 0
    };

    this.spinner.show();
    if (this.editingCategory) {
      this.chainsService.updateChainCategory(this.chainId, this.editingCategory._id, payload).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Category updated successfully');
          this.cancelCategoryForm();
          this.loadCategories();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to update category');
        }
      });
    } else {
      this.chainsService.createChainCategory(this.chainId, payload).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Category created successfully');
          this.cancelCategoryForm();
          this.loadCategories();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to create category');
        }
      });
    }
  }

  deleteCategory(cat: any): void {
    Swal.fire({
      title: 'Delete Category?',
      text: `"${cat.name}" will be permanently deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.chainsService.deleteChainCategory(this.chainId, cat._id).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Category deleted');
            this.loadCategories();
          },
          error: (err: any) => {
            this.spinner.hide();
            this.toastr.error(err.error?.response?.message || 'Failed to delete category');
          }
        });
      }
    });
  }

  // ── Product helpers ─────────────────────────────────

  loadProducts(): void {
    if (!this.chainId) return;
    this.productLoading = true;
    this.chainsService.getChainProducts(this.chainId).subscribe({
      next: (res: any) => {
        this.products = res.response?.data?.products || res.response?.data || res.products || [];
        this.productLoading = false;
      },
      error: (err: any) => {
        this.productLoading = false;
        if (err.status !== 404) {
          this.toastr.error(err.error?.response?.message || 'Failed to load products');
        }
      }
    });
  }

  openAddProduct(): void {
    this.editingProduct = null;
    this.productForm.reset({ name: '', description: '', barcode: '', price: 0, categoryId: '', isVisible: true });
    this.showProductForm = true;
  }

  openEditProduct(product: any): void {
    this.editingProduct = product;
    this.productForm.patchValue({
      name: product.name,
      description: product.description || '',
      barcode: product.barcode || '',
      price: product.price || 0,
      categoryId: product.categoryId?._id || product.categoryId || '',
      isVisible: product.isVisible !== false
    });
    this.showProductForm = true;
  }

  cancelProductForm(): void {
    this.showProductForm = false;
    this.editingProduct = null;
    this.productForm.reset();
  }

  submitProductForm(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }
    const formValue = this.productForm.value;
    const payload: any = {
      name: formValue.name,
      description: formValue.description || undefined,
      price: formValue.price,
      isVisible: formValue.isVisible
    };
    if (formValue.barcode) payload['barcode'] = formValue.barcode;
    if (formValue.categoryId) payload['categoryId'] = formValue.categoryId;

    this.spinner.show();
    if (this.editingProduct) {
      this.chainsService.updateChainProduct(this.chainId, this.editingProduct._id, payload).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Product updated successfully');
          this.cancelProductForm();
          this.loadProducts();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to update product');
        }
      });
    } else {
      this.chainsService.createChainProduct(this.chainId, payload).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Product created successfully');
          this.cancelProductForm();
          this.loadProducts();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to create product');
        }
      });
    }
  }

  deleteProduct(product: any): void {
    Swal.fire({
      title: 'Delete Product?',
      text: `"${product.name}" will be permanently removed from the chain catalog.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.chainsService.deleteChainProduct(this.chainId, product._id).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Product deleted');
            this.loadProducts();
          },
          error: (err: any) => {
            this.spinner.hide();
            this.toastr.error(err.error?.response?.message || 'Failed to delete product');
          }
        });
      }
    });
  }

  getCategoryName(categoryId: any): string {
    if (!categoryId) return '—';
    const id = typeof categoryId === 'object' ? categoryId._id : categoryId;
    const cat = this.categories.find(c => c._id === id);
    return cat ? cat.name : '—';
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
